import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const MaskedLine = ({ children, delay, className }) => (
  <span className={`block overflow-hidden ${className || ""}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onJoin }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const rotateX = useTransform(sy, [-0.5, 0.5], [10, -10]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-10, 10]);
  const driftX = useTransform(sx, [-0.5, 0.5], [-18, 18]);
  const driftY = useTransform(sy, [-0.5, 0.5], [-14, 14]);

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen items-center overflow-hidden pt-24"
      data-testid="hero-section"
    >
      <img
        src="https://images.pexels.com/photos/14515698/pexels-photo-14515698.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
        alt=""
        aria-hidden
        className="hero-spotlight pointer-events-none absolute inset-0 h-full w-full object-cover opacity-30"
      />
      <div className="pointer-events-none absolute inset-0 bg-paper/55" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 md:grid-cols-[1.2fr_1fr] md:px-12">
        <div>
          <MaskedLine delay={0.15}>
            <span className="text-xs uppercase tracking-[0.35em] text-ink/60">
              A wine club with nothing to hide
            </span>
          </MaskedLine>
          <h1 className="mt-6 font-serif text-5xl leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            <MaskedLine delay={0.3}>Free to join.</MaskedLine>
            <MaskedLine delay={0.45}>
              <em className="text-wine">No catch.</em>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.65} className="mt-8 max-w-md">
            <p className="text-base leading-relaxed text-ink/70 md:text-lg" data-testid="hero-subheadline">
              Wine without the usual club fees. Hand-picked bottles of all kinds,
              shipped to members nationwide across the US.
            </p>
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <button
              data-testid="hero-join-button"
              onClick={onJoin}
              className="group flex items-center gap-3 rounded-full bg-wine px-8 py-4 text-sm uppercase tracking-widest text-paper transition-colors duration-300 hover:bg-ink"
            >
              Join the club — free
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <button
              data-testid="hero-membership-link"
              onClick={() => document.getElementById("membership")?.scrollIntoView({ behavior: "smooth" })}
              className="border-b border-gold pb-1 text-sm uppercase tracking-widest text-ink/70 transition-colors duration-300 hover:text-wine"
            >
              See membership
            </button>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-10 text-xs uppercase tracking-[0.25em] text-ink/45"
            data-testid="hero-proof-line"
          >
            No fees · No commitment · Skip, swap or cancel anytime
          </motion.p>
        </div>

        <div className="hidden justify-center md:flex" style={{ perspective: 1200 }}>
          <motion.div
            animate={{ y: [0, -14, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          >
            <motion.img
              src="/assets/logo.png"
              alt="Unlabeled Wine Club stamp"
              data-testid="hero-logo-stamp"
              initial={{ opacity: 0, scale: 0.85, rotate: -6 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              style={{ rotateX, rotateY, x: driftX, y: driftY }}
              className="aspect-square w-[380px] rounded-full object-cover mix-blend-multiply lg:w-[440px]"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
